import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ChevronDown, ExternalLink } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { sanitizeAssistantVisibleContent } from '../lib/messageModel';
import { useTypewriterContent } from '../hooks/useTypewriterContent';

type DocumentationCitation = {
  index: number;
  title: string;
  url: string;
};

type DomParserConstructor = new () => DOMParser;

export function decorateDocumentationCitations(
  sanitizedHtml: string,
  allowedDocumentationUrls: Iterable<string>,
  Parser: DomParserConstructor | undefined =
    typeof DOMParser === 'undefined' ? undefined : DOMParser,
): { citations: DocumentationCitation[]; html: string } {
  if (!Parser) return { citations: [], html: sanitizedHtml };

  const parsed = new Parser().parseFromString(`<div>${sanitizedHtml}</div>`, 'text/html');
  const container = parsed.body.firstElementChild;
  if (!container) return { citations: [], html: sanitizedHtml };

  const citations: DocumentationCitation[] = [];
  const allowedUrls = new Set(
    Array.from(allowedDocumentationUrls).flatMap((value) => {
      try {
        const url = new URL(value);
        return url.origin === 'https://feedback.charitystack.com' &&
          (url.pathname === '/docs' || url.pathname.startsWith('/docs/'))
          ? [url.toString()]
          : [];
      } catch {
        return [];
      }
    }),
  );
  const citationIndexByUrl = new Map<string, number>();
  container.querySelectorAll('a[href]').forEach((anchor) => {
    const rawHref = anchor.getAttribute('href');
    if (!rawHref) return;

    let url: URL;
    try {
      url = new URL(rawHref, 'https://feedback.charitystack.com');
    } catch {
      return;
    }
    if (
      url.origin !== 'https://feedback.charitystack.com' ||
      (url.pathname !== '/docs' && !url.pathname.startsWith('/docs/'))
    ) {
      return;
    }

    const normalizedUrl = url.toString();
    if (!allowedUrls.has(normalizedUrl)) return;
    let index = citationIndexByUrl.get(normalizedUrl);
    if (!index) {
      index = citations.length + 1;
      citationIndexByUrl.set(normalizedUrl, index);
      citations.push({
        index,
        title: citationTitle(anchor.textContent || '', url),
        url: normalizedUrl,
      });
    }

    anchor.textContent = String(index);
    anchor.setAttribute('aria-label', `Open source ${index}: ${citations[index - 1].title}`);
    anchor.setAttribute('class', 'assistant-doc-citation');
    anchor.setAttribute('rel', 'noreferrer');
    anchor.setAttribute('target', '_blank');
  });

  container.querySelectorAll('p').forEach((paragraph) => {
    if (!/^sources?:\s*$/i.test(paragraph.textContent?.trim() || '')) return;
    const list = paragraph.nextElementSibling;
    if (
      !list ||
      !['UL', 'OL'].includes(list.tagName) ||
      !list.querySelector('a.assistant-doc-citation')
    ) {
      return;
    }
    list.remove();
    paragraph.remove();
  });

  return { citations, html: container.innerHTML };
}

function citationTitle(value: string, url: URL) {
  const trimmed = value.trim();
  if (trimmed && !/^https?:\/\//i.test(trimmed)) return trimmed.slice(0, 120);
  if (url.pathname === '/docs') return 'CharityStack documentation';
  const slug = url.pathname.split('/').filter(Boolean).pop() || 'Documentation';
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
    .slice(0, 120);
}

function DocumentationSourcesDisclosure({
  citations,
}: {
  citations: DocumentationCitation[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sourceLabel = citations.length === 1 ? '1 source' : `${citations.length} sources`;

  return (
    <div className="relative mt-2.5 inline-block">
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
        className="group inline-flex min-h-8 items-center gap-2 rounded-full border border-edge/80 bg-paper-light/70 py-1 pl-1.5 pr-2.5 text-xs font-medium text-ink-muted transition hover:border-sage/30 hover:bg-paper-light hover:text-ink focus:outline-none focus:ring-2 focus:ring-sage/30"
      >
        <span className="flex items-center pl-1" aria-hidden="true">
          {citations.slice(0, 3).map((citation, index) => (
            <span
              key={citation.url}
              className={`flex h-5 w-5 items-center justify-center rounded-full border border-edge bg-white text-[9px] font-semibold text-ink-muted shadow-sm ${
                index > 0 ? '-ml-1.5' : ''
              }`}
            >
              {citation.index}
            </span>
          ))}
        </span>
        <span>{sourceLabel}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-150 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded ? (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="absolute bottom-full left-0 z-30 mb-2 w-[min(300px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-edge/80 bg-white p-1.5 shadow-lg shadow-ink/10"
        >
          <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            <BookOpen className="h-3 w-3" />
            Sources
          </div>
          {citations.map((citation) => (
            <a
              key={citation.url}
              href={citation.url}
              target="_blank"
              rel="noreferrer"
              className="group/source flex min-h-9 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-ink no-underline transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sage/30"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-edge bg-white text-[9px] font-semibold text-ink-muted">
                {citation.index}
              </span>
              <span className="min-w-0 flex-1 truncate">{citation.title}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-muted opacity-60 transition group-hover/source:opacity-100" />
            </a>
          ))}
        </motion.div>
      ) : null}
    </div>
  );
}

export function MarkdownMessage({
  allowedDocumentationUrls = [],
  animateText = false,
  content,
  isStreaming = false,
  onAnimationComplete,
}: {
  allowedDocumentationUrls?: string[];
  animateText?: boolean;
  content: string;
  isStreaming?: boolean;
  onAnimationComplete?: () => void;
}) {
  const safeContent = useMemo(() => sanitizeAssistantVisibleContent(content), [content]);
  const visibleContent = useTypewriterContent({
    content: safeContent,
    enabled: animateText,
    onComplete: onAnimationComplete,
  });
  const rendered = useMemo(() => {
    const rawHtml = marked.parse(visibleContent, {
      async: false,
      breaks: true,
      gfm: true,
    }) as string;

    return decorateDocumentationCitations(
      DOMPurify.sanitize(rawHtml),
      allowedDocumentationUrls,
    );
  }, [allowedDocumentationUrls, visibleContent]);

  return (
    <div className="relative">
      <div
        className="break-words [&_a]:text-sage [&_a]:underline [&_a.assistant-doc-citation]:mx-0.5 [&_a.assistant-doc-citation]:inline-flex [&_a.assistant-doc-citation]:h-4 [&_a.assistant-doc-citation]:min-w-4 [&_a.assistant-doc-citation]:translate-y-[-1px] [&_a.assistant-doc-citation]:items-center [&_a.assistant-doc-citation]:justify-center [&_a.assistant-doc-citation]:rounded-full [&_a.assistant-doc-citation]:border [&_a.assistant-doc-citation]:border-sage/25 [&_a.assistant-doc-citation]:bg-sage/10 [&_a.assistant-doc-citation]:px-1 [&_a.assistant-doc-citation]:text-[9px] [&_a.assistant-doc-citation]:font-semibold [&_a.assistant-doc-citation]:leading-none [&_a.assistant-doc-citation]:text-sage [&_a.assistant-doc-citation]:no-underline [&_a.assistant-doc-citation:hover]:bg-sage/15 [&_code]:rounded [&_code]:bg-white/80 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_li+li]:mt-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-0 [&_p+p]:mt-2 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-white/80 [&_pre]:p-3 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
        dangerouslySetInnerHTML={{ __html: rendered.html }}
      />
      {rendered.citations.length > 0 && !isStreaming ? (
        <DocumentationSourcesDisclosure citations={rendered.citations} />
      ) : null}
      {isStreaming || animateText ? (
        <motion.span
          aria-hidden="true"
          className="ml-0.5 inline-block h-4 w-1 translate-y-0.5 rounded-full bg-ink-muted"
          animate={{ opacity: [0.25, 0.9, 0.25] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }}
        />
      ) : null}
    </div>
  );
}
